import { EnrollmentFlow, RecoveryCodes, useToast } from "@uzverse/ui";
import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type { EnrollmentResult, StepUpProof, TotpEnrollment } from "../api/types.js";
import { useSession } from "../state/session.js";
import { ErrorNote, useCommonT, useErrorText } from "./common.js";

type View =
  | { kind: "idle" }
  | { kind: "proof"; action: "recovery" | "reenroll" }
  | { kind: "codes"; codes: string[] }
  | { kind: "enroll"; enrollment: TotpEnrollment };

/**
 * Account security for normal users: regenerate recovery codes or re-enroll the authenticator.
 * Both need the current password and a current code. The Owner has neither here: their second
 * factor is reset only from the server command line (DESIGN §10).
 */
export function SecuritySettings() {
  const { t } = useTranslation("web");
  const tc = useCommonT();
  const errorText = useErrorText();
  const toast = useToast();
  const { services, signIn, user } = useSession();
  const [view, setView] = useState<View>({ kind: "idle" });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmed = useRef<EnrollmentResult | null>(null);

  if (user?.isOwner) {
    return (
      <section className="card" aria-labelledby="security-title">
        <h2 id="security-title">{t("settings.security.title")}</h2>
        <p className="muted">{t("settings.security.ownerNote")}</p>
      </section>
    );
  }

  const reset = () => {
    setView({ kind: "idle" });
    setPassword("");
    setCode("");
    setError(null);
  };

  const submitProof = (e: FormEvent) => {
    e.preventDefault();
    if (view.kind !== "proof") return;
    const proof: StepUpProof = { password, code: code.trim() };
    setBusy(true);
    setError(null);
    const next =
      view.action === "recovery"
        ? services.api
            .regenerateRecoveryCodes(proof)
            .then((codes): View => ({ kind: "codes", codes }))
        : services.api
            .beginReenrollment(proof)
            .then((enrollment): View => ({ kind: "enroll", enrollment }));
    next
      .then((v) => {
        setPassword("");
        setCode("");
        setView(v);
      })
      .catch((err: unknown) => setError(errorText(err)))
      .finally(() => setBusy(false));
  };

  return (
    <section className="card" aria-labelledby="security-title">
      <h2 id="security-title">{t("settings.security.title")}</h2>
      {view.kind === "idle" && (
        <>
          <p className="muted">{t("settings.security.intro")}</p>
          <div className="stack">
            <div>
              <button
                type="button"
                className="btn btn-block"
                onClick={() => setView({ kind: "proof", action: "recovery" })}
              >
                {t("settings.security.recoveryAction")}
              </button>
              <small className="muted block">{t("settings.security.recoveryHint")}</small>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-block"
                onClick={() => setView({ kind: "proof", action: "reenroll" })}
              >
                {t("settings.security.reenrollAction")}
              </button>
              <small className="muted block">{t("settings.security.reenrollHint")}</small>
            </div>
          </div>
        </>
      )}
      {view.kind === "proof" && (
        <form onSubmit={submitProof} className="stack">
          <p className="muted">{t("settings.security.intro")}</p>
          <label className="field">
            <span>{t("settings.security.password")}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label className="field">
            <span>{t("settings.security.code")}</span>
            <input
              className="input-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
            />
          </label>
          <ErrorNote message={error} />
          <div className="row">
            <button type="submit" className="btn btn-primary" disabled={busy || code.length !== 6}>
              {t("settings.security.continue")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={reset}>
              {t("actions.cancel", { ns: "common" })}
            </button>
          </div>
        </form>
      )}
      {view.kind === "codes" && (
        <RecoveryCodes
          t={tc}
          codes={view.codes}
          onDone={() => {
            reset();
            toast.show(t("settings.security.recoveryDone"));
          }}
        />
      )}
      {view.kind === "enroll" && (
        <EnrollmentFlow
          t={tc}
          seed={{ otpauthUri: view.enrollment.otpauthUri, secret: view.enrollment.manualKey }}
          errorText={errorText}
          confirm={async (c) => {
            const done = await services.api.confirmTotpEnrollment(view.enrollment.enrollmentId, c);
            confirmed.current = done;
            return done.recoveryCodes;
          }}
          onFinished={() => {
            if (confirmed.current) signIn(confirmed.current);
            confirmed.current = null;
            reset();
            toast.show(t("settings.security.reenrolled"));
          }}
        />
      )}
    </section>
  );
}
